import { Document, Packer, Paragraph, TextRun, HeadingLevel, PageBreak, TableOfContents, Header, Footer, AlignmentType, BorderStyle, Table, TableRow, TableCell, WidthType, VerticalAlign, convertInchesToTwip } from 'docx';
import { saveAs } from 'file-saver';

const TEMPLATE_STYLES = {
  'professional-blue': {
    primary: '1e40af',
    secondary: '3b82f6',
    accent: 'dbeafe',
    fontFamily: 'Georgia'
  },
  'executive-red': {
    primary: 'dc2626',
    secondary: 'ef4444',
    accent: 'fee2e2',
    fontFamily: 'Palatino Linotype'
  },
  'modern-minimal': {
    primary: '18181b',
    secondary: '3f3f46',
    accent: 'f4f4f5',
    fontFamily: 'Calibri'
  },
  'classic-green': {
    primary: '166534',
    secondary: '22c55e',
    accent: 'dcfce7',
    fontFamily: 'Times New Roman'
  }
};

// Helper function to parse HTML table and convert to docx Table
function parseHtmlTable(tableHtml, style) {
  // Extract rows using regex
  const rowMatches = tableHtml.match(/<tr[^>]*>[\s\S]*?<\/tr>/gi) || [];
  const tableRows = [];
  
  for (let i = 0; i < rowMatches.length; i++) {
    const rowHtml = rowMatches[i];
    const isHeader = rowHtml.includes('<th');
    const cellMatches = rowHtml.match(/<t[hd][^>]*>([\s\S]*?)<\/t[hd]>/gi) || [];
    
    const cells = [];
    for (let j = 0; j < cellMatches.length; j++) {
      const cellContent = cellMatches[j]
        .replace(/<t[hd][^>]*>/gi, '')
        .replace(/<\/t[hd]>/gi, '')
        .replace(/<[^>]+>/g, '') // Strip any nested HTML
        .trim();
      
      cells.push(
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: cellContent,
                  bold: isHeader,
                  size: isHeader ? 22 : 20,
                  color: isHeader ? style.primary : '334155',
                  font: style.fontFamily
                })
              ],
              alignment: j > 0 ? AlignmentType.RIGHT : AlignmentType.LEFT
            })
          ],
          shading: isHeader ? { fill: style.accent } : undefined,
          verticalAlign: VerticalAlign.CENTER,
          margins: {
            top: 80,
            bottom: 80,
            left: 120,
            right: 120
          }
        })
      );
    }
    
    if (cells.length > 0) {
      tableRows.push(new TableRow({ children: cells }));
    }
  }
  
  if (tableRows.length === 0) return null;
  
  return new Table({
    rows: tableRows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: 'cbd5e1' },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: 'cbd5e1' },
      left: { style: BorderStyle.SINGLE, size: 1, color: 'cbd5e1' },
      right: { style: BorderStyle.SINGLE, size: 1, color: 'cbd5e1' },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'cbd5e1' },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'cbd5e1' }
    }
  });
}

// Helper to extract text content from HTML, preserving tables
function parseContentWithTables(content, style, docChildren) {
  // Check if content has tables
  if (!content.includes('<table')) {
    return false; // Let regular processing handle it
  }
  
  // Split content by tables
  const parts = content.split(/(<table[\s\S]*?<\/table>)/gi);
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (!part) continue;
    
    if (part.toLowerCase().startsWith('<table')) {
      // Parse and add table
      const table = parseHtmlTable(part, style);
      if (table) {
        docChildren.push(table);
        docChildren.push(new Paragraph({ spacing: { after: 300 } })); // Space after table
      }
    } else {
      // Regular content - strip HTML and add as paragraphs
      const cleanText = part
        .replace(/<\/p>\s*<p>/gi, '\n\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .trim();
      
      if (cleanText) {
        const paragraphs = cleanText.split('\n\n');
        for (let j = 0; j < paragraphs.length; j++) {
          const para = paragraphs[j].trim();
          if (para) {
            if (para.startsWith('- ') || para.startsWith('• ')) {
              docChildren.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: para.substring(2),
                      size: 24,
                      color: '334155',
                      font: style.fontFamily
                    })
                  ],
                  bullet: { level: 0 },
                  spacing: { after: 150 }
                })
              );
            } else {
              docChildren.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: para,
                      size: 24,
                      color: '334155',
                      font: style.fontFamily
                    })
                  ],
                  spacing: { after: 300 },
                  alignment: AlignmentType.JUSTIFIED
                })
              );
            }
          }
        }
      }
    }
  }
  
  return true; // Content was processed
}

export async function exportToWord(proposal, sections, completedSections, templateId, token, apiUrl) {
  const style = TEMPLATE_STYLES[templateId] || TEMPLATE_STYLES['executive-red'];
  
  // Fetch all completed sections data
  const sectionsWithContent = [];
  for (let i = 0; i < completedSections.length; i++) {
    const sectionId = completedSections[i];
    const section = sections.find(s => s.id === sectionId);
    if (!section) continue;
    
    try {
      const res = await fetch(
        `${apiUrl}/ai/proposals/${proposal.id}/sections/${sectionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const sectionData = await res.json();
        if (sectionData.content) {
          sectionsWithContent.push({
            ...section,
            content: sectionData.content,
            citations: sectionData.citations || []
          });
        }
      }
    } catch (err) {
      console.error(`Failed to fetch section ${sectionId}:`, err);
    }
  }

  // Build document children
  const docChildren = [];

  // Cover Page
  docChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'BUSINESS PLAN',
          size: 24,
          color: style.secondary,
          font: style.fontFamily
        })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: proposal?.idea_title || 'Untitled Proposal',
          size: 56,
          bold: true,
          color: style.primary,
          font: style.fontFamily
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: '________________________________________',
          color: style.secondary
        })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: proposal?.description || 'A comprehensive business proposal for the UK Innovator Founder Visa application.',
          size: 24,
          color: '64748b',
          font: style.fontFamily
        })
      ],
      spacing: { after: 800 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Prepared: ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`,
          size: 22,
          color: '64748b'
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Category: ${proposal?.category || 'Technology'}`,
          size: 22,
          color: '64748b'
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [new PageBreak()]
    })
  );

  // Table of Contents Header
  docChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Table of Contents',
          size: 36,
          bold: true,
          color: style.primary,
          font: style.fontFamily
        })
      ],
      spacing: { after: 400 }
    })
  );

  // TOC entries
  for (let i = 0; i < sectionsWithContent.length; i++) {
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${i + 1}. ${sectionsWithContent[i].title}`,
            size: 24,
            color: '1e293b'
          }),
          new TextRun({
            text: `\t\t\t\tPage ${i + 3}`,
            size: 22,
            color: '94a3b8'
          })
        ],
        spacing: { after: 200 },
        border: {
          bottom: {
            color: 'e2e8f0',
            space: 1,
            style: BorderStyle.SINGLE,
            size: 6
          }
        }
      })
    );
  }

  docChildren.push(
    new Paragraph({
      children: [new PageBreak()]
    })
  );

  // Content sections
  for (let i = 0; i < sectionsWithContent.length; i++) {
    const section = sectionsWithContent[i];
    
    // Section header
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `SECTION ${i + 1}`,
            size: 20,
            color: style.secondary,
            font: style.fontFamily
          })
        ],
        spacing: { after: 100 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: section.title,
            size: 36,
            bold: true,
            color: style.primary,
            font: style.fontFamily
          })
        ],
        spacing: { after: 100 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: '________________',
            color: style.secondary
          })
        ],
        spacing: { after: 400 }
      })
    );

    // Section content - check for tables first, then split by paragraphs
    const hasTable = parseContentWithTables(section.content, style, docChildren);
    
    if (!hasTable) {
      // No tables - process as regular paragraphs
      const paragraphs = section.content.split('\n\n');
      for (let j = 0; j < paragraphs.length; j++) {
        const para = paragraphs[j].trim();
        if (para) {
          // Check if it's a bullet point
          if (para.startsWith('- ') || para.startsWith('• ')) {
            docChildren.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: para.substring(2),
                    size: 24,
                    color: '334155',
                    font: style.fontFamily
                  })
                ],
                bullet: { level: 0 },
                spacing: { after: 150 }
              })
            );
          } else {
            docChildren.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: para,
                    size: 24,
                    color: '334155',
                    font: style.fontFamily
                  })
                ],
                spacing: { after: 300 },
                alignment: AlignmentType.JUSTIFIED
              })
            );
          }
        }
      }
    }

    // Citations
    if (section.citations && section.citations.length > 0) {
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: 'References',
              size: 22,
              bold: true,
              color: style.primary
            })
          ],
          spacing: { before: 400, after: 200 }
        })
      );

      for (let k = 0; k < section.citations.length; k++) {
        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `[${k + 1}] ${section.citations[k]}`,
                size: 20,
                color: '64748b',
                italics: true
              })
            ],
            spacing: { after: 100 }
          })
        );
      }
    }

    // Page break between sections (except last)
    if (i < sectionsWithContent.length - 1) {
      docChildren.push(
        new Paragraph({
          children: [new PageBreak()]
        })
      );
    }
  }

  // Create document
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440,
              right: 1440,
              bottom: 1440,
              left: 1440
            }
          }
        },
        children: docChildren
      }
    ]
  });

  // Generate and download
  const blob = await Packer.toBlob(doc);
  const filename = `${(proposal?.idea_title || 'Business-Plan').replace(/[^a-zA-Z0-9]/g, '-')}.docx`;
  saveAs(blob, filename);
  
  return filename;
}
